import React, { useEffect, useState } from "react";
import { ShieldCheck, ShieldOff, KeyRound, Download, RefreshCw } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/* Onglet Sécurité de la console admin : 2FA TOTP + journal des connexions. */
export default function AdminSecurityPanel() {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [setup, setSetup] = useState(null); // { qr_data_url, secret }
  const [codeIn, setCodeIn] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);
  const [disableCode, setDisableCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [s, l] = await Promise.all([
        apiClient.get("/admin/security/status"),
        apiClient.get("/admin/security/login-logs"),
      ]);
      setStatus(s.data);
      setLogs(l.data.logs || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post("/admin/security/totp/setup");
      setSetup(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const activate = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post("/admin/security/totp/activate", { code: codeIn });
      setBackupCodes(data.backup_codes);
      setSetup(null);
      setCodeIn("");
      toast.success("2FA activée sur le compte admin");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    if (!window.confirm("Désactiver la double authentification ?")) return;
    setBusy(true);
    try {
      await apiClient.post("/admin/security/totp/disable", { code: disableCode });
      setDisableCode("");
      toast.success("2FA désactivée");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const downloadCodes = () => {
    const txt = ["Codes de secours NelDaari Console (usage unique)", "", ...backupCodes].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([txt], { type: "text/plain" }));
    a.download = "neldaari-codes-secours.txt";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (!status) return <div className="text-mute text-sm">Chargement…</div>;

  return (
    <div className="space-y-6" data-testid="admin-security-panel">
      {/* 2FA */}
      <div className="bg-white border border-sand rounded-2xl p-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 font-semibold">
            {status.totp_enabled
              ? <ShieldCheck className="w-5 h-5 text-brand-green" />
              : <ShieldOff className="w-5 h-5 text-brand-red" />}
            Double authentification (application)
          </div>
          <span className={`text-xs font-bold rounded-full px-3 py-1 ${status.totp_enabled ? "bg-brand-green/10 text-brand-green" : "bg-brand-red/10 text-brand-red"}`} data-testid="totp-status-badge">
            {status.totp_enabled ? `Activée · ${status.backup_codes_remaining} code(s) de secours restant(s)` : "Désactivée"}
          </span>
        </div>

        {!status.totp_enabled && !setup && !backupCodes && (
          <button onClick={startSetup} disabled={busy} data-testid="totp-setup-btn"
            className="mt-4 rounded-full bg-ink text-white px-5 py-2.5 text-sm font-semibold disabled:opacity-60">
            Activer la 2FA
          </button>
        )}

        {setup && (
          <div className="mt-4 grid sm:grid-cols-[auto_1fr] gap-4 items-start">
            <img src={setup.qr_data_url} alt="QR code 2FA" className="w-44 h-44 border border-sand rounded-xl" data-testid="totp-qr" />
            <div className="space-y-3 text-sm">
              <p>1. Scannez ce QR code avec <b>Google Authenticator, Microsoft Authenticator ou Authy</b>.</p>
              <p className="text-xs text-mute break-all">Clé manuelle : <code className="bg-elevated px-1.5 py-0.5 rounded">{setup.secret}</code></p>
              <p>2. Saisissez le code à 6 chiffres affiché :</p>
              <div className="flex gap-2">
                <input value={codeIn} onChange={(e) => setCodeIn(e.target.value)} placeholder="123456" inputMode="numeric"
                  data-testid="totp-activate-code"
                  className="rounded-xl border border-sand px-4 py-2.5 w-36 text-center font-mono tracking-widest focus:outline-none focus:border-ink" />
                <button onClick={activate} disabled={busy || codeIn.trim().length < 6} data-testid="totp-activate-btn"
                  className="rounded-full bg-brand-green text-white px-5 py-2.5 text-sm font-semibold disabled:opacity-60">
                  Activer
                </button>
              </div>
            </div>
          </div>
        )}

        {backupCodes && (
          <div className="mt-4 rounded-xl border border-brand-green/30 bg-brand-green/5 p-4" data-testid="totp-backup-codes">
            <div className="font-semibold text-sm mb-2">Codes de secours (usage unique) — conservez-les en lieu sûr, ils ne seront plus affichés :</div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 font-mono text-sm">
              {backupCodes.map((c) => <span key={c} className="bg-white border border-sand rounded-lg px-2 py-1 text-center">{c}</span>)}
            </div>
            <button onClick={downloadCodes} data-testid="totp-download-codes"
              className="mt-3 inline-flex items-center gap-2 rounded-full border border-sand hover:border-ink px-4 py-2 text-sm font-semibold">
              <Download className="w-4 h-4" /> Télécharger (.txt)
            </button>
          </div>
        )}

        {status.totp_enabled && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <input value={disableCode} onChange={(e) => setDisableCode(e.target.value)} placeholder="Code 2FA"
              data-testid="totp-disable-code"
              className="rounded-xl border border-sand px-4 py-2 w-36 text-center font-mono text-sm focus:outline-none focus:border-ink" />
            <button onClick={disable} disabled={busy || !disableCode.trim()} data-testid="totp-disable-btn"
              className="rounded-full border border-brand-red text-brand-red px-4 py-2 text-sm font-semibold disabled:opacity-50">
              Désactiver la 2FA
            </button>
          </div>
        )}
      </div>

      {/* Journal des connexions */}
      <div className="bg-white border border-sand rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 font-semibold"><KeyRound className="w-5 h-5 text-brand-red" /> Journal des connexions admin</div>
          <button onClick={load} className="text-mute hover:text-ink" data-testid="security-logs-refresh"><RefreshCw className="w-4 h-4" /></button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="security-logs-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-mute border-b border-sand">
                <th className="py-2 pr-3">Date / heure</th>
                <th className="py-2 pr-3">Identifiant</th>
                <th className="py-2 pr-3">IP</th>
                <th className="py-2 pr-3">Étape</th>
                <th className="py-2">Résultat</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 && <tr><td colSpan={5} className="py-4 text-mute">Aucune connexion enregistrée.</td></tr>}
              {logs.map((l) => (
                <tr key={l.log_id} className="border-b border-sand/60">
                  <td className="py-2 pr-3 whitespace-nowrap">{new Date(l.ts).toLocaleString("fr-FR")}</td>
                  <td className="py-2 pr-3">{l.email}</td>
                  <td className="py-2 pr-3">{l.ip || "—"}</td>
                  <td className="py-2 pr-3">{l.step}</td>
                  <td className="py-2">
                    <span className={`text-xs font-bold rounded-full px-2 py-0.5 ${l.success ? "bg-brand-green/10 text-brand-green" : "bg-brand-red/10 text-brand-red"}`}>
                      {l.success ? "Succès" : "Échec"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
